import { useApiPost, useApiQuery } from '@/api/main/query'
import { PaginatedCollection } from '@/api/types'
import { Currencies } from '@/entities/curency'
import { Roles } from '@/entities/roles'
import { USER_ROLE_KEY } from '@/features/auth'
import { NewWithdrawModel, WithdrawsModel, WithdrawStatus } from '@/features/withdraws'
import { withdrawsAPIMerchant } from '@/features/withdraws/api'
import { DEFAULT_TAKE } from '@/shared/constants/constants'
import { useToast } from '@/shared/hooks'
import { getStorageItem } from '@/shared/lib/storage/localStorage'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
type UseWithdrawsListProps = {
  id?: string // Поиск по ID заявки

  countryto?: string // Поиск по стране получателя
  currencyto?: Currencies // Поиск по валюте получателя
  methodto?: string // Поиск по платежному методу получателя
  bankto?: string // Поиск по банку получателя

  amountfrom?: number // Поиск по сумме от (в валюте получателя)
  amountto?: number // Поиск по сумме до (в валюте получателя)

  createdfromutc: string // Поиск по дате создания от (UTC)
  createdtoutc: string // Поиск по дате создания до (UTC)

  confirmedFrom?: string // Поиск по дате подтверждения от
  confirmedTo?: string // Поиск по дате подтверждения до

  preq?: string // Поиск по платежному реквизиту
  withdrawStatus?: WithdrawStatus // Поиск по статусу заявки

  merchantFromId?: string // Поиск по ID мерчанта, создавшего заявку
  assignedTraderId?: string // Поиск по ID трейдера, назначенного на заявку
  refreshTrigger?: number
  take?: number
}
interface Props {
  filters: UseWithdrawsListProps
  onCreateSuccess?: () => void
  onCreateSettled?: () => void
}

export const useWithdrawsMerchant = ({ filters, onCreateSuccess, onCreateSettled }: Props) => {
  const { addToast } = useToast()
  const queryClient = useQueryClient()
  const userRole = getStorageItem<Roles | null>(USER_ROLE_KEY, null)

  // Pagination state
  const [skip, setSkip] = useState(0)

  useEffect(() => {
    setSkip(0)
  }, [JSON.stringify(filters)]) // при любом изменении фильтров – обнуляем skip

  const {
    id,
    countryto,
    currencyto,
    methodto,
    bankto,
    amountfrom,
    amountto,
    createdfromutc,
    createdtoutc,
    confirmedFrom,
    confirmedTo,
    preq,
    withdrawStatus,
    merchantFromId,
    assignedTraderId,
    refreshTrigger,
    take = DEFAULT_TAKE,
  } = filters

  const queryKey = [
    'withdraws',
    skip,
    take,
    id,
    countryto,
    currencyto,
    methodto,
    bankto,
    amountfrom,
    amountto,
    createdfromutc,
    createdtoutc,
    confirmedFrom,
    confirmedTo,
    preq,
    withdrawStatus,
    merchantFromId,
    assignedTraderId,
    refreshTrigger,
  ].filter(Boolean)

  const queryParams: Record<string, string | number | boolean | undefined> = {
    skip,
    take,
  }

  if (id) {
    queryParams.id = id
  }

  if (countryto) {
    queryParams.countryto = countryto
  }

  if (currencyto !== undefined) {
    queryParams.currencyto = currencyto
  }

  if (methodto) {
    queryParams.methodto = methodto
  }

  if (bankto) {
    queryParams.bankto = bankto
  }

  if (amountfrom !== undefined) {
    queryParams.amountfrom = amountfrom
  }

  if (amountto !== undefined) {
    queryParams.amountto = amountto
  }

  if (createdfromutc) {
    queryParams.createdfromutc = createdfromutc
  }

  if (createdtoutc) {
    queryParams.createdtoutc = createdtoutc
  }

  if (confirmedFrom) {
    queryParams.confirmedFrom = confirmedFrom
  }

  if (confirmedTo) {
    queryParams.confirmedTo = confirmedTo
  }

  if (preq) {
    queryParams.preq = preq
  }

  if (withdrawStatus !== undefined) {
    queryParams.withdrawStatus = withdrawStatus
  }

  if (merchantFromId) {
    queryParams.merchantFromId = merchantFromId
  }

  if (assignedTraderId) {
    queryParams.assignedTraderId = assignedTraderId
  }

  if (refreshTrigger) {
    queryParams.refreshTrigger = refreshTrigger
  }

  // Запрос списка выводов
  const {
    data: queryWithdrawsData,
    error: withdrawsError,
    isLoading: withdrawsIsLoad,
    refetch: refetchWithdraws,
  } = useApiQuery<PaginatedCollection<WithdrawsModel[]>>(
    queryKey,
    withdrawsAPIMerchant.list,
    queryParams,
    {
      enabled: userRole === Roles.Merchant,
      staleTime:5000
    }
  )

  // Создание нового вывода (мерчант)
  const { mutate: createWithdrawMutation, isPending: isPendingCreateWithdraw } = useApiPost<
    WithdrawsModel,
    NewWithdrawModel
  >(withdrawsAPIMerchant.create, {
    onSuccess: () => {
      addToast('Успех!', 'Исходящий платеж создан', 'success')
      queryClient.invalidateQueries({ queryKey })
      onCreateSuccess?.()
    },
    onError: (error) => {
      addToast('Ошибка!', error.message, 'error')
    },
    onSettled: () => {
      onCreateSettled?.()
    },
  })

  // Методы для работы с API
  const createWithdraw = (data: NewWithdrawModel) => {
    createWithdrawMutation(data)
  }

  const handlePageChange = (newSkip: number) => {
    setSkip(newSkip)
  }

  const invalidateWithdraws = () => {
    queryClient.invalidateQueries({ queryKey })
  }

  return {
    // Data
    data: {
      withdrawsData: queryWithdrawsData,
      withdrawsError,
      isLoading: withdrawsIsLoad,
      skip,
      totalItems: queryWithdrawsData?.count ?? 0,
      items: queryWithdrawsData?.items || [],
    },

    // Loading states
    loading: {
      isPendingCreateWithdraw,
      withdrawsIsLoad,
    },

    // Methods
    methods: {
      createWithdraw,
      handlePageChange,
      refetchWithdraws,
      invalidateWithdraws,
    },
  }
}
